// TerminosPopup.js — Ventana para leer los términos y condiciones del proyecto.
//
// Se abre desde la casilla «Acepto términos y condiciones» (registro de la web y
// modal «Completa tu información»). El texto lo escribe el administrador en la
// ficha → Login y registro → «Términos y condiciones» (config_json.terminos_condiciones).
// «Acepto» cierra la ventana y marca la casilla; «Cerrar» solo la cierra.
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useProject } from '../contexts/ProjectContext';

const Velo = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000001;          /* por encima del registro y de «Completa tu información» */
  background: rgba(18, 10, 34, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Caja = styled.div`
  width: min(720px, 100%);
  max-height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
  overflow: hidden;
  font-family: inherit;
`;

const Cabecera = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 22px;
  border-bottom: 1px solid #ede9fe;
  h2 { flex: 1; margin: 0; font-size: 1.25rem; line-height: 1.25; color: ${p => p.$color}; }
  button {
    width: 34px; height: 34px; border-radius: 50%; border: none; flex: none;
    background: #f5f3ff; color: ${p => p.$color}; font-size: 20px; line-height: 1; cursor: pointer;
  }
`;

/* El texto se desplaza dentro de la ventana; la página de atrás no se mueve. */
const Texto = styled.div`
  padding: 18px 22px;
  overflow-y: auto;
  color: #374151;
  font-size: 0.95rem;
  line-height: 1.6;
  text-align: left;
  h1, h2, h3, h4 { color: #1b0b43; line-height: 1.3; margin: 1.1em 0 0.4em; }
  p { margin: 0 0 0.8em; }
  ul, ol { padding-left: 1.3em; margin: 0 0 0.8em; }
  a { color: ${p => p.$acento}; }
  > :first-child { margin-top: 0; }
`;

const Pie = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 22px;
  border-top: 1px solid #ede9fe;
  button {
    border-radius: 10px; padding: 10px 20px; font: inherit; font-weight: 700; cursor: pointer;
    border: 1px solid ${p => p.$acento};
  }
  .secundario { background: #fff; color: ${p => p.$acento}; }
  .primario { background: ${p => p.$acento}; color: #fff; }
  @media (max-width: 480px) { button { flex: 1; } }
`;

/** Texto de los términos del proyecto (vacío si no se ha escrito). */
export const useTerminos = () => {
  const { proyecto } = useProject() || {};
  const t = proyecto?.config_json?.terminos_condiciones || {};
  const texto = (v) => (v && typeof v === 'object' ? (v.es || Object.values(v)[0] || '') : (v || ''));
  const contenido = texto(t.contenido).trim();
  return {
    hay: contenido.replace(/<[^>]*>/g, '').trim() !== '',
    titulo: texto(t.titulo) || 'Términos y condiciones',
    contenido,
    textoAceptar: texto(t.texto_aceptar) || 'Acepto',
    acento: t.color_acento || '#7c3aed',
    colorTitulo: t.color_titulo || '#1b0b43',
  };
};

const TerminosPopup = ({ abierto, onCerrar, onAceptar }) => {
  const t = useTerminos();
  const cerrarRef = useRef(null);

  useEffect(() => {
    if (!abierto) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onCerrar?.(); } };
    document.addEventListener('keydown', onKey, true);
    cerrarRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey, true);
  }, [abierto, onCerrar]);

  if (!abierto || !t.hay) return null;

  return createPortal(
    <Velo onMouseDown={(e) => { if (e.target === e.currentTarget) onCerrar?.(); }}>
      <Caja role="dialog" aria-modal="true" aria-labelledby="terminos_titulo">
        <Cabecera $color={t.colorTitulo}>
          <h2 id="terminos_titulo">{t.titulo}</h2>
          <button ref={cerrarRef} type="button" aria-label="Cerrar" onClick={onCerrar}>×</button>
        </Cabecera>
        <Texto $acento={t.acento} dangerouslySetInnerHTML={{ __html: t.contenido }} />
        <Pie $acento={t.acento}>
          <button type="button" className="secundario" onClick={onCerrar}>Cerrar</button>
          {onAceptar && (
            <button type="button" className="primario" onClick={() => { onAceptar(); onCerrar?.(); }}>{t.textoAceptar}</button>
          )}
        </Pie>
      </Caja>
    </Velo>,
    document.body
  );
};

export default TerminosPopup;
